/**
 * useCanvasRenderer Hook
 * 
 * Manages the CanvasRenderer Web Worker lifecycle.
 * Renders canvas frames in a Web Worker using OffscreenCanvas,
 * bypassing Fabric.js for fast hidden-tab recording.
 * 
 * Usage:
 *   const { init, start, stop, updateLayout, updateWebcamFrame } = useCanvasRenderer({
 *     onFrame: (bitmap, timestamp) => { writeToMSTG(bitmap, timestamp); }
 *   });
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { RECORDING_FLAGS } from '../constants/recordingFlags';
import type { CanvasRendererMessage, LayoutData } from '../worker/canvasRenderer.worker';

export type { LayoutData } from '../worker/canvasRenderer.worker';

export interface UseCanvasRendererOptions {
  fps?: number;
  onFrame?: (bitmap: ImageBitmap, frameNumber: number, timestamp: number) => void;
  onInitialized?: () => void;
  onStarted?: () => void;
  onStopped?: () => void;
  onError?: (error: string) => void;
}

export interface UseCanvasRendererReturn {
  init: (width: number, height: number) => void;
  start: () => void;
  stop: () => void;
  updateLayout: (layout: LayoutData) => void;
  updateWebcamFrame: (bitmap: ImageBitmap) => void;
  updateScreenFrame: (bitmap: ImageBitmap) => void;
  isRunning: boolean;
  isInitialized: boolean;
  workerEnabled: boolean;
}

const MAX_RESTART_ATTEMPTS = 3;
const BASE_RESTART_DELAY_MS = 100;
const MAX_RESTART_DELAY_MS = 2000;

export function useCanvasRenderer(options: UseCanvasRendererOptions = {}): UseCanvasRendererReturn {
  const { fps = 30, onFrame, onInitialized, onStarted, onStopped, onError } = options;
  
  const workerRef = useRef<Worker | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const restartAttemptsRef = useRef(0);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldRestartRef = useRef(false);
  const pendingStartRef = useRef(false);
  
  const callbacksRef = useRef({ onFrame, onInitialized, onStarted, onStopped, onError });
  callbacksRef.current = { onFrame, onInitialized, onStarted, onStopped, onError };
  
  const fpsRef = useRef(fps);
  fpsRef.current = fps;

  const workerEnabled = RECORDING_FLAGS.OFFSCREEN_CANVAS && typeof OffscreenCanvas !== 'undefined';

  const createWorker = useCallback(() => {
    if (!workerEnabled) {
      return null;
    }
    
    try {
      const worker = new Worker(
        new URL('../worker/canvasRenderer.worker.ts', import.meta.url),
        { type: 'module' }
      );
      
      worker.onmessage = (event: MessageEvent<CanvasRendererMessage>) => {
        const { type, frameNumber, timestamp, videoBitmap, error } = event.data;
        
        switch (type) {
          case 'frame':
            if (videoBitmap && frameNumber !== undefined && timestamp !== undefined) {
              callbacksRef.current.onFrame?.(videoBitmap, frameNumber, timestamp);
            }
            break;
          case 'initialized':
            setIsInitialized(true);
            callbacksRef.current.onInitialized?.();
            break;
          case 'started':
            setIsRunning(true);
            restartAttemptsRef.current = 0;
            callbacksRef.current.onStarted?.();
            break;
          case 'stopped':
            setIsRunning(false);
            callbacksRef.current.onStopped?.();
            break;
          case 'error':
            callbacksRef.current.onError?.(error || 'Unknown worker error');
            break;
        }
      };
      
      worker.onerror = (error) => {
        console.error('[useCanvasRenderer] Worker error:', error);
        callbacksRef.current.onError?.(error.message);
        
        if (shouldRestartRef.current && restartAttemptsRef.current < MAX_RESTART_ATTEMPTS) {
          const delay = Math.min(
            BASE_RESTART_DELAY_MS * Math.pow(2, restartAttemptsRef.current),
            MAX_RESTART_DELAY_MS
          );
          restartAttemptsRef.current++;
          console.warn(`[useCanvasRenderer] Worker crashed, attempting restart ${restartAttemptsRef.current}/${MAX_RESTART_ATTEMPTS} in ${delay}ms`);
          
          restartTimeoutRef.current = setTimeout(() => {
            workerRef.current = createWorker();
            if (workerRef.current && pendingStartRef.current) {
              workerRef.current.postMessage({ type: 'start', fps: fpsRef.current });
            }
          }, delay);
        }
      };
      
      return worker;
    } catch (error) {
      console.error('[useCanvasRenderer] Failed to create worker:', error);
      return null;
    }
  }, [workerEnabled]);

  const init = useCallback((width: number, height: number) => {
    if (!workerEnabled) {
      console.warn('[useCanvasRenderer] OffscreenCanvas not supported');
      return;
    }
    
    if (workerRef.current) {
      workerRef.current.terminate();
    }
    
    workerRef.current = createWorker();
    if (!workerRef.current) return;
    
    const offscreen = new OffscreenCanvas(width, height);
    
    workerRef.current.postMessage(
      { type: 'init', canvas: offscreen },
      [offscreen]
    );
  }, [workerEnabled, createWorker]);

  const start = useCallback(() => {
    if (!workerRef.current || !isInitialized) {
      console.warn('[useCanvasRenderer] Worker not initialized');
      return;
    }
    
    shouldRestartRef.current = true;
    pendingStartRef.current = true;
    workerRef.current.postMessage({ type: 'start', fps: fpsRef.current });
  }, [isInitialized]);

  const stop = useCallback(() => {
    shouldRestartRef.current = false;
    pendingStartRef.current = false;
    
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'stop' });
    }
  }, []);

  const updateLayout = useCallback((layout: LayoutData) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: 'updateLayout', layout });
  }, []);

  const updateWebcamFrame = useCallback((bitmap: ImageBitmap) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage(
      { type: 'updateWebcamFrame', webcamBitmap: bitmap },
      [bitmap]
    );
  }, []);

  const updateScreenFrame = useCallback((bitmap: ImageBitmap) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage(
      { type: 'updateScreenFrame', screenBitmap: bitmap },
      [bitmap]
    );
  }, []);

  useEffect(() => {
    return () => {
      shouldRestartRef.current = false;
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  return {
    init,
    start,
    stop,
    updateLayout,
    updateWebcamFrame,
    updateScreenFrame,
    isRunning,
    isInitialized,
    workerEnabled,
  };
}
